import {
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// --- Facades ---
import { AppearanceFacade } from '@facades/appearance.facade';
import { AuthFacade } from '@facades/auth.facade';
import { CartFacade } from '@facades/cart.facade';

// --- Interfaces ---
import type { IStore } from '@interfaces/store';
import { IMenuItem } from '../../interfaces/sidebar';
import { SubSink } from 'subsink';
import { filter, shareReplay, take } from 'rxjs';
import {
	MENUS_COMMON_LOGGED,
	MENUS_BY_ROLE,
	MENUS_VISITOR,
	MENU_LOGOUT,
} from './menus';
import { EUserProfileIds } from '../../interfaces/user';
import { ParametersFacade } from '../../facades/parameters.facade';
import { FavoritesDialogComponent } from '../dialogs/favorites-dialog/favorites-dialog.component';
import { CartDialogComponent } from '../dialogs/cart-dialog/cart-dialog.component';
import { TenantsFacade } from '../../facades/tenants.facade';
import { IFeatures, IModules } from '../../interfaces/tenants';

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
	ref?: DynamicDialogRef;
	private subs = new SubSink();

	@ViewChild('settingsMenu') settingsMenu!: Menu;

	address!: IStore['displayedAddress'];
	corporateName!: IStore['corporateName'];
	hasPackingFeature: boolean = this.tenantsFacade.hasFlag(
		'enableProductPacking'
	);
	menuItems: IMenuItem[] = [];
	settingsMenuItems: MenuItem[] = [];
	isLoggingOut = false;
	badge: number = 0;
	cartDialogOpen = false;
	favoritesDialogOpen = false;
	isMobile = false;
	isMobileSidebarOpen = false;
	profile: EUserProfileIds = 0;

	constructor(
		private _router: Router,
		public _dialogService: DialogService,
		private cartFacade: CartFacade,
		private appearanceFacade: AppearanceFacade,
		private parametersFacade: ParametersFacade,
		private authFacade: AuthFacade,
		private tenantsFacade: TenantsFacade
	) {}

	@HostListener('window:scroll')
	onWindowScroll() {
		if (this.settingsMenu.visible) {
			this.settingsMenu.hide();
		}
	}

	@HostListener('window:resize', [])
	checkScreenSize() {
		this.isMobile = window.innerWidth <= 650;

		if (!this.isMobile && this.isMobileSidebarOpen) {
			this.isMobileSidebarOpen = false;
			document.body.style.overflow = '';
		}
	}

	toggleMobileSidebar() {
		this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
		document.body.style.overflow = this.isMobileSidebarOpen ? 'hidden' : ''; // trava scroll
	}

	ngOnInit() {
		this.checkScreenSize();

		// Escuta resize em tempo real
		window.addEventListener('resize', () => this.checkScreenSize());

		this.subs.add(
			this.authFacade.authState$.pipe(take(1)).subscribe(({ user }) => {
				const actions = {
					login: () => this.login(),
					register: () => this.register(),
					logout: () => this.logout(),
					cart: () => this.cartDialog(),
					favorites: () => this.favoritesDialog(),
					openSettingsMenu: (event: MouseEvent) => this.openSettingsMenu(event),
				};

				if (!user) {
					this.menuItems = MENUS_VISITOR(actions);
					return;
				}

				this.profile = user.profileId ?? EUserProfileIds.GUEST;
				const roleMenu = MENUS_BY_ROLE(actions)[this.profile] || [];

				this.menuItems = [
					...MENUS_COMMON_LOGGED,
					...roleMenu,
					MENU_LOGOUT(actions),
				].map(item => ({
					...item,
					activeOptions: this.defineActiveOptions(item),
				}));
			})
		);

		this.subs.add(
			this._router.events
				.pipe(filter(event => event instanceof NavigationEnd))
				.subscribe(() => {
					this.menuItems = this.menuItems.map(item => ({
						...item,
						activeOptions: this.defineActiveOptions(item),
					}));
				})
		);

		this.settingsMenuItems = [
			{
				label: 'Speedchat',
				icon: 'fa-solid fa-comments',
				command: () => this._router.navigate(['/integrations/speedchat']),
				module: 'speedChat' as keyof IModules,
			},
			{
				label: 'Suporte e Ajuda',
				icon: 'fa-solid fa-circle-question',
				command: () => this._router.navigate(['/preferences/support']),
			},
			{
				label: 'Solicitações',
				icon: 'fa-solid fa-person-circle-question',
				command: () => this._router.navigate(['/access-requests']),
				profileVisible: [EUserProfileIds.APPLICATION_ADMIN],
			},
		]
			.filter(item => !item.module || this.hasModule(item.module))
			.filter(
				item =>
					!item.profileVisible || item.profileVisible.includes(this.profile)
			);
	}

	hoveredItem: MenuItem | null = null;

	isActive(item: IMenuItem): boolean {
		const currentUrl = this._router.url;

		// Se o carrinho estiver aberto, só o Carrinho fica ativo
		if (this.cartDialogOpen) {
			return item.label === 'Carrinho';
		}

		// Se os favoritos estiverem abertos, só Favoritos fica ativo
		if (this.favoritesDialogOpen) {
			return item.label === 'Favoritos';
		}

		// Itens de rota normais
		if (
			item.route &&
			this._router.isActive(item.route, item.activeOptions?.exact ?? false)
		) {
			return true;
		}

		if (item.label === 'Carrinho' && currentUrl.startsWith('/account/cart')) {
			return true;
		}

		if (
			item.label === 'Favoritos' &&
			currentUrl.startsWith('/account/favorites')
		) {
			return true;
		}

		return false;
	}

	private lockScroll() {
		document.body.style.overflow = 'hidden';
	}

	private unlockScroll() {
		document.body.style.overflow = '';
	}

	onSettingsMenuHide() {
		this.unlockScroll();
	}

	openSettingsMenu(event: MouseEvent) {
		this.settingsMenu.toggle(event);
		this.lockScroll();
	}

	get cartTotalItems() {
		return this.cartFacade.totalItems || 0;
	}

	get stockDecimalPlaces() {
		return (this.parametersFacade.parameters?.stockDecimalPlaces || 0) > 0
			? 1
			: 0;
	}

	login() {
		this.subs.add(
			this.authFacade.logout().subscribe(() => this._router.navigate(['/auth']))
		);
	}

	private defineActiveOptions(item: IMenuItem) {
		const currentUrl = this._router.url;

		if (item.route === '/') {
			return { exact: true };
		}

		if (
			item.route === '/account' &&
			(currentUrl.startsWith('/account/cart') ||
				currentUrl.startsWith('/account/orders') ||
				currentUrl.startsWith('/account/favorites'))
		) {
			return { exact: true }; // não marca o pai
		}

		if (
			item.route === '/dashboard' &&
			currentUrl.startsWith('/dashboard/orders')
		) {
			return { exact: true };
		}

		return { exact: false };
	}

	register() {
		// this._router.navigate(['/auth/register']);
	}

	logout() {
		this.isLoggingOut = true;

		this.subs.add(
			this.authFacade
				.logout()
				.pipe(shareReplay({ refCount: true }))
				.subscribe(() => {
					this.isLoggingOut = false;
					this.cartFacade.cleanCartCache();
					this.appearanceFacade.loadedTheme = false;
					this.appearanceFacade.logo = '';
					this._router.navigate(['auth']);
				})
		);
	}

	get appearance() {
		const { logo, useTheme } = this.appearanceFacade;
		return { logo, useTheme };
	}

	favoritesDialog() {
		this.closeCurrentDialog();
		this.favoritesDialogOpen = true;

		this.ref = this._dialogService.open(FavoritesDialogComponent, {
			header: 'Favoritos',
			modal: true,
			styleClass: 'border-round overflow-hidden',
			width: '540px',
		});

		this.subs.add(
			this.ref.onClose.subscribe(() => {
				this.favoritesDialogOpen = false;
				this.ref = undefined;
			})
		);
	}

	cartDialog() {
		this.closeCurrentDialog();
		this.cartDialogOpen = true;

		if (this.hasPackingFeature) {
			this.ref = this._dialogService.open(CartDialogComponent, {
				header: 'Carrinho',
				modal: true,
				styleClass: 'border-round overflow-hidden',
				width: '540px',
			});

			this.subs.add(
				this.ref.onClose.subscribe(() => {
					this.cartDialogOpen = false;
					this.ref = undefined;
				})
			);
		} else {
			this._router.navigate(['account', 'cart']);
		}
	}

	private closeCurrentDialog() {
		if (this.ref) {
			this.ref.close();
			this.ref = undefined;
		}

		this.cartDialogOpen = false;
		this.favoritesDialogOpen = false;
	}

	shouldDisplay(item: IMenuItem): boolean {
		if (item.module && !this.hasModule(item.module)) {
			return false;
		}

		return true;
	}

	hasModule(module: keyof IModules) {
		return this.tenantsFacade.hasModule(module);
	}

	hasFeature(feature: keyof IFeatures) {
		return this.tenantsFacade.hasFeatures(feature);
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}
}
